import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import PratusSelect, {
  type OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { IncidentTypes } from 'components/IncidentConfig/IncidentTypes';
import { useAors } from 'hooks/useAors';
import { getLatLongFromGeometry } from '../LayersTab/utils/getLatLongFromGeometry';
import type { Geometry } from 'geojson';
import { NULL_VALUE } from 'constants/displayConstants';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { useIncidentLayerSync } from '../LayersTab/utils/useIncidentLayerSync';
import NewIncidentDialog from 'components/IncidentConfig/NewIncidentDialog';
import { getIncidentIcon } from './utils/incidentIconUtils';
import { IncidentMapButton } from './IncidentMapButton';
import { IncidentCardBody } from './IncidentCardBody';

export type IncidentListItemData = {
  id: string;
  name: string;
  aor: string;
  incidentTypes: string[];
  aorIds: string[];
  createdAt: string;
  latLong: { latitude: number; longitude: number } | null;
  locationGeometry: Geometry | null;
  currentPeriodId?: string;
};

const isExerciseType = (type: string): boolean => {
  const normalizedType = type.toLowerCase().trim();
  return normalizedType === 'exercise';
};

const Incidents: React.FC = () => {
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { mapView, isMapVisible } = useUscgMapContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewIncidentDialog, setShowNewIncidentDialog] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<OptionType[] | null>(null);
  const [selectedAors, setSelectedAors] = useState<OptionType[] | null>(null);
  const [hiddenIncidentIds, setHiddenIncidentIds] = useState<string[]>([]);

  const typeOptions = useMemo<OptionType[]>(() => {
    return IncidentTypes.filter((type) => !isExerciseType(type)).map(
      (type) => ({
        value: type,
        label: type,
      })
    );
  }, []);

  const aorOptions = useMemo<OptionType[]>(() => {
    return aors.map((aor) => ({
      value: aor.id,
      label: aor.name,
    }));
  }, [aors]);

  const aorNameById = useMemo(() => {
    return new Map(aors.map((aor) => [aor.id, aor.name]));
  }, [aors]);

  const filteredIncidents = useMemo<IncidentListItemData[]>(() => {
    const typeValues = (selectedTypes ?? []).map((option) => option.value);
    const aorValues = (selectedAors ?? []).map((option) => option.value);

    return incidents
      .filter(
        (incident) => !(incident.incident_types || []).some(isExerciseType)
      )
      .map((incident) => {
        const aorIds = incident.areas_of_responsibility || [];
        const aorNames = aorIds.map((aorId) => aorNameById.get(aorId) || aorId);

        let location = incident.location;
        if (typeof location === 'string') {
          try {
            location = JSON.parse(location);
          } catch {
            location = null;
          }
        }

        const incidentLatLong = location
          ? getLatLongFromGeometry(location)
          : null;

        const latLong = incidentLatLong;

        return {
          id: incident.id,
          name: incident.name,
          aor: aorNames.join(', ') || NULL_VALUE,
          incidentTypes: (incident.incident_types || []).filter(
            (type) => !isExerciseType(type)
          ),
          aorIds,
          createdAt: dayjs(incident.incident_created).format(
            'MMMM D, YYYY HH:mm'
          ),
          latLong,
          locationGeometry: (location as Geometry | null) ?? null,
          currentPeriodId: incident.current_period,
        };
      })
      .filter((incident) => {
        const matchesSearch = incident.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        const matchesType =
          typeValues.length === 0 ||
          incident.incidentTypes.some((type) => typeValues.includes(type));

        const matchesAor =
          aorValues.length === 0 ||
          incident.aorIds.some((aorId) => aorValues.includes(aorId));

        return matchesSearch && matchesType && matchesAor;
      });
  }, [incidents, searchTerm, selectedTypes, selectedAors, aorNameById]);

  const filteredIncidentIds = useMemo(
    () => filteredIncidents.map((incident) => incident.id),
    [filteredIncidents]
  );

  const hiddenFilteredIncidentIds = useMemo(
    () => filteredIncidentIds.filter((id) => hiddenIncidentIds.includes(id)),
    [filteredIncidentIds, hiddenIncidentIds]
  );

  const isAllVisible = useMemo(() => {
    if (filteredIncidentIds.length === 0) return false;
    return hiddenFilteredIncidentIds.length === 0;
  }, [filteredIncidentIds.length, hiddenFilteredIncidentIds.length]);

  useIncidentLayerSync({
    mapView,
    visibleIncidents: filteredIncidents,
    hiddenIncidentIds: hiddenFilteredIncidentIds,
  });

  const toggleIncidentSelection = useCallback((incidentId: string) => {
    setHiddenIncidentIds((prevIds) => {
      if (prevIds.includes(incidentId)) {
        return prevIds.filter((id) => id !== incidentId);
      }

      return [...prevIds, incidentId];
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    if (filteredIncidentIds.length === 0) return;

    const filteredSet = new Set(filteredIncidentIds);

    setHiddenIncidentIds((prevIds) => {
      if (isAllVisible) {
        return [...new Set([...prevIds, ...filteredIncidentIds])];
      }

      return prevIds.filter((id) => !filteredSet.has(id));
    });
  }, [filteredIncidentIds, isAllVisible]);

  return (
    <div className="d-flex flex-column gap-3 px-2 pt-2 pb-5">
      <div className="bg-bgdark rounded border border-neutral-500 p-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div className="d-flex align-items-baseline flex-shrink-0 gap-2">
            <span>Incidents</span>
            <span>({filteredIncidents.length} active)</span>
          </div>

          <div className="d-flex flex-column flex-lg-row align-items-lg-center w-lg-auto w-100 gap-2">
            <div
              className="flex-lg-grow-0 flex-grow-1"
              style={{ minWidth: 0, maxWidth: '100%' }}
            >
              <StylishNewSearchBar
                placeholder="Search incidents..."
                value={searchTerm}
                onChangeSearchTerm={(value: string) =>
                  setSearchTerm(value.trim())
                }
              />
            </div>

            <StylishNewButton
              className="button--primary"
              onClick={() => setShowNewIncidentDialog(true)}
            >
              <SharedIcon iconName="add" size="sm" />
              <span>New Incident</span>
            </StylishNewButton>

            {isMapVisible && (
              <StylishNewButton
                className="button--icon"
                title={
                  isAllVisible
                    ? 'Hide all incidents from map'
                    : 'Show all incidents on map'
                }
                aria-label={
                  isAllVisible ? 'Hide all incidents' : 'Show all incidents'
                }
                onClick={handleToggleAll}
              >
                <SharedIcon
                  iconName={isAllVisible ? 'visibility' : 'visibility_off'}
                  size="sm"
                />
              </StylishNewButton>
            )}
          </div>
        </div>
      </div>

      <div className="row g-2">
        <div className="col-lg-6 col-12">
          <div className="bg-bgdark rounded border border-neutral-500 p-3">
            <PratusSelect
              label="Type"
              isMulti
              isClearable
              options={typeOptions}
              value={selectedTypes}
              onChange={(value) => {
                const nextValues = Array.isArray(value)
                  ? (value as OptionType[])
                  : value
                    ? [value as OptionType]
                    : [];
                setSelectedTypes(nextValues.length > 0 ? nextValues : null);
              }}
            />
          </div>
        </div>

        <div className="col-lg-6 col-12">
          <div className="bg-bgdark rounded border border-neutral-500 p-3">
            <PratusSelect
              label="AOR"
              isMulti
              isClearable
              options={aorOptions}
              value={selectedAors}
              onChange={(value) => {
                const nextValues = Array.isArray(value)
                  ? (value as OptionType[])
                  : value
                    ? [value as OptionType]
                    : [];
                setSelectedAors(nextValues.length > 0 ? nextValues : null);
              }}
            />
          </div>
        </div>
      </div>

      <div className="d-grid gap-2">
        {filteredIncidents.map((incident, index) => (
          <CollapsibleCard
            key={incident.id}
            sectionKey={`incident-card-${incident.id}`}
            defaultCollapsed={index !== 0}
            className="card-outlined"
            contentClassName="bg-bgdark"
            arrowPosition="left"
            headerLeft={
              <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                <div className="d-flex align-items-center gap-2">
                  {isMapVisible && (
                    <div
                      style={{ width: '24px', flexShrink: 0 }}
                      className="d-flex align-items-center justify-content-center"
                    >
                      {incident.latLong &&
                        getIncidentIcon(incident.incidentTypes) !== null && (
                          <StylishNewCheckbox
                            className="mt-0"
                            checked={!hiddenIncidentIds.includes(incident.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleIncidentSelection(incident.id);
                            }}
                            aria-label={
                              !hiddenIncidentIds.includes(incident.id)
                                ? `Hide ${incident.name} on map`
                                : `Show ${incident.name} on map`
                            }
                          />
                        )}
                    </div>
                  )}
                  <span className="fw-semibold">{incident.name}</span>
                </div>
              </div>
            }
            headerRight={
              incident.latLong ? (
                <IncidentMapButton incident={incident} />
              ) : null
            }
          >
            <IncidentCardBody incident={incident} />
          </CollapsibleCard>
        ))}
      </div>

      {showNewIncidentDialog && (
        <NewIncidentDialog onClose={() => setShowNewIncidentDialog(false)} />
      )}
    </div>
  );
};

export default Incidents;
